import {
    Fragment,
    useState,
    useId
} from "react";
import {
    ComponentHeader,
    AnatomySection,
    ExampleSection,
    getSectionToc,
    PropsTable
} from "./shared";
import {
    type IModalProps,
    NCoreUIKitTheme,
    TextInput,
    Button,
    Switch,
    Modal,
    Card,
    Text
} from "ncore-ui-kit";
import {
    useDocsI18n
} from "../../i18n";
import type {
    DocsTranslate
} from "../../anatomy/types";
import {
    BulletList,
    Paragraph,
    CodeBlock,
    Article,
    Preview,
    Section
} from "../../components/content";

const NAME = "Modal";

const USAGE_CODE = `const [isOpen, setIsOpen] = useState(false);

<Modal
    accessibilityLabel="Share report"
    onOpenChange={setIsOpen}
    isShowSurface={true}
    isOpen={isOpen}
    id="share-modal"
>
    <Text
        variant="titleLargeSize"
        role="heading"
    >
        Share report
    </Text>
    <Button
        onPress={() => setIsOpen(false)}
        title="Done"
    />
</Modal>`;

const BARE_CODE = `<Modal
    accessibilityLabel="Free surface"
    onOpenChange={setIsOpen}
    isOpen={isOpen}
    id="bare-modal"
>
    <Card variant="elevated">
        ...
    </Card>
</Modal>`;

const ALIGNS = [
    "center",
    "free"
] as const;

type ModalShowcaseProps = Pick<IModalProps, "alignContent" | "isAnimated" | "isDisabledOverlay" | "isOverlayVisible" | "isScaleAnimated" | "isShowClose"> & {
    isDismissible?: boolean;
    title: string;
};

type ModalHeadingProps = {
    description: string;
    title: string;
    id?: string;
};

const handleModalPress = ({
    closeAnimation
}: {
    closeAnimation: () => void;
}) => closeAnimation();

const ModalHeading = ({
    description,
    title,
    id
}: ModalHeadingProps) => {
    const {
        spaces
    } = NCoreUIKitTheme.useContext();

    return <Fragment>
        <Text
            style={{
                paddingEnd: spaces.spacingXl
            }}
            variant="titleLargeSize"
            role="heading"
            color="high"
            id={id}
        >
            {title}
        </Text>
        <Text
            variant="bodyMediumSize"
            color="mid"
        >
            {description}
        </Text>
    </Fragment>;
};

const ModalShowcase = ({
    isDismissible = true,
    isDisabledOverlay,
    isOverlayVisible,
    isScaleAnimated,
    alignContent,
    isShowClose,
    isAnimated,
    title
}: ModalShowcaseProps) => {
    const {
        t
    } = useDocsI18n();

    const id = useId();

    const titleId = `${id}-title`;

    const [
        isOpen,
        setIsOpen
    ] = useState(false);

    const isFree = alignContent === "free";

    return <Fragment>
        <Button
            onPress={() => setIsOpen(true)}
            variant="outline"
            type="neutral"
            title={title}
        />
        <Modal
            onOverlayPress={isDismissible ? handleModalPress : undefined}
            accessibilityLabel={isFree ? t("sample.search") : undefined}
            aria-labelledby={isFree ? undefined : titleId}
            isDisabledOverlay={isDisabledOverlay}
            isOverlayVisible={isOverlayVisible}
            onClosed={() => setIsOpen(false)}
            isScaleAnimated={isScaleAnimated}
            alignContent={alignContent}
            isShowClose={isShowClose}
            isAnimated={isAnimated}
            isShowSurface={true}
            isOpen={isOpen}
            id={id}
        >
            {
                isFree ?
                    <TextInput
                        placeholder={t("modalPage.searchPlaceholder")}
                        title={t("sample.search")}
                        spreadBehaviour="stretch"
                    />
                :
                    <Fragment>
                        <ModalHeading
                            description={t("modalPage.demoDescription")}
                            title={t("modalPage.demoTitle")}
                            id={titleId}
                        />
                        <Button
                            onPress={() => setIsOpen(false)}
                            title={t("modalPage.close")}
                            spreadBehaviour="stretch"
                        />
                    </Fragment>
            }
        </Modal>
    </Fragment>;
};

export const getToc = (t: DocsTranslate) => getSectionToc([
    {
        title: t("section.overview"),
        id: "overview"
    },
    {
        title: t("modalPage.surfaceTitle"),
        id: "surface"
    },
    {
        title: t("modalPage.alignTitle"),
        id: "align"
    },
    {
        title: t("modalPage.dismissTitle"),
        id: "dismiss"
    },
    {
        title: t("modalPage.motionTitle"),
        id: "motion"
    },
    {
        title: t("section.accessibility"),
        id: "accessibility"
    },
    {
        title: t("section.props"),
        id: "props"
    }
], NAME, t);

const ModalPage = () => {
    const {
        shapeConfigs
    } = NCoreUIKitTheme.useContext();

    const {
        t
    } = useDocsI18n();

    const [
        isShareOpen,
        setIsShareOpen
    ] = useState(false);

    const [
        isLinkShared,
        setIsLinkShared
    ] = useState(true);

    const [
        isBareOpen,
        setIsBareOpen
    ] = useState(false);

    return <Article>
        <ComponentHeader
            lead={t("modalPage.lead")}
            name={NAME}
        />
        <Section
            title={t("section.overview")}
            id="overview"
        >
            <Paragraph>
                {t("modalPage.overviewText")}
            </Paragraph>
            <Preview
                caption={t("modalPage.overviewCaption")}
            >
                <Button
                    onPress={() => setIsShareOpen(true)}
                    title={t("modalPage.shareReport")}
                />
                <Modal
                    accessibilityLabel={t("modalPage.shareReport")}
                    onOpenChange={setIsShareOpen}
                    id="docs-modal-share"
                    isOpen={isShareOpen}
                    isShowSurface={true}
                >
                    <ModalHeading
                        description={t("modalPage.shareDescription")}
                        title={t("modalPage.shareReport")}
                    />
                    <Switch
                        subTitle={t("modalPage.linkAccessHint")}
                        onCheckedChange={setIsLinkShared}
                        title={t("modalPage.linkAccess")}
                        spreadBehaviour="stretch"
                        isChecked={isLinkShared}
                        isFlip={true}
                    />
                    <Button
                        onPress={() => setIsShareOpen(false)}
                        title={t("modalPage.done")}
                        spreadBehaviour="stretch"
                    />
                </Modal>
            </Preview>
            <CodeBlock
                code={USAGE_CODE}
                language="tsx"
            />
        </Section>
        <AnatomySection
            name={NAME}
        />
        <Section
            title={t("modalPage.surfaceTitle")}
            id="surface"
        >
            <Paragraph>
                {t("modalPage.surfaceText")}
            </Paragraph>
            <Preview>
                <ModalShowcase
                    title={t("modalPage.withSurface")}
                />
                <Button
                    title={t("modalPage.customSurface")}
                    onPress={() => setIsBareOpen(true)}
                    variant="outline"
                    type="neutral"
                />
                <Modal
                    accessibilityLabel={t("modalPage.customSurface")}
                    onOpenChange={setIsBareOpen}
                    id="docs-modal-bare"
                    isOpen={isBareOpen}
                >
                    <Card
                        style={{
                            maxWidth: shapeConfigs.dialogMaxWidth
                        }}
                        variant="elevated"
                    >
                        <Card.Header>
                            <Card.Title>
                                {t("modalPage.demoTitle")}
                            </Card.Title>
                            <Card.Description>
                                {t("modalPage.bareDescription")}
                            </Card.Description>
                        </Card.Header>
                        <Card.Footer>
                            <Button
                                onPress={() => setIsBareOpen(false)}
                                title={t("modalPage.close")}
                            />
                        </Card.Footer>
                    </Card>
                </Modal>
            </Preview>
            <CodeBlock
                code={BARE_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("modalPage.alignTitle")}
            id="align"
        >
            <Paragraph>
                {t("modalPage.alignText")}
            </Paragraph>
            <Preview>
                {
                    ALIGNS.map((alignContent) => <ModalShowcase
                        isShowClose={alignContent !== "free"}
                        alignContent={alignContent}
                        title={alignContent}
                        key={alignContent}
                    />)
                }
            </Preview>
        </Section>
        <Section
            title={t("modalPage.dismissTitle")}
            id="dismiss"
        >
            <Paragraph>
                {t("modalPage.dismissText")}
            </Paragraph>
            <Preview
                caption={t("modalPage.dismissCaption")}
            >
                <ModalShowcase
                    title={t("modalPage.dismissible")}
                />
                <ModalShowcase
                    title={t("modalPage.noOverlayPress")}
                    isDismissible={false}
                />
                <ModalShowcase
                    title={t("modalPage.locked")}
                    isDisabledOverlay={true}
                    isShowClose={false}
                />
            </Preview>
        </Section>
        <Section
            title={t("modalPage.motionTitle")}
            id="motion"
        >
            <Paragraph>
                {t("modalPage.motionText")}
            </Paragraph>
            <Preview>
                <ModalShowcase
                    title={t("modalPage.fade")}
                />
                <ModalShowcase
                    title="isScaleAnimated={false}"
                    isScaleAnimated={false}
                />
                <ModalShowcase
                    title="isAnimated={false}"
                    isAnimated={false}
                />
                <ModalShowcase
                    title="isOverlayVisible={false}"
                    isOverlayVisible={false}
                />
            </Preview>
        </Section>
        <Section
            title={t("section.accessibility")}
            id="accessibility"
        >
            <BulletList
                items={[
                    t("modalPage.a11yRole"),
                    t("modalPage.a11yLabel"),
                    t("modalPage.a11yFocus"),
                    t("modalPage.a11yDismiss"),
                    t("modalPage.a11yScrim"),
                    t("modalPage.a11yClose"),
                    t("modalPage.a11yStack"),
                    t("modalPage.a11yMotion")
                ]}
            />
        </Section>
        <Section
            title={t("section.props")}
            id="props"
        >
            <PropsTable
                rows={[
                    {
                        description: t("modalPage.propId"),
                        type: "string",
                        name: "id"
                    },
                    {
                        description: t("modalPage.propIsOpen"),
                        name: "isOpen",
                        type: "boolean"
                    },
                    {
                        description: t("modalPage.propOnOpenChange"),
                        type: "(isOpen: boolean) => void",
                        name: "onOpenChange"
                    },
                    {
                        description: t("modalPage.propChildren"),
                        type: "ReactNode",
                        name: "children"
                    },
                    {
                        description: t("modalPage.propIsShowSurface"),
                        defaultValue: "false",
                        name: "isShowSurface",
                        type: "boolean"
                    },
                    {
                        description: t("modalPage.propIsShowClose"),
                        defaultValue: "true",
                        name: "isShowClose",
                        type: "boolean"
                    },
                    {
                        description: t("modalPage.propAlignContent"),
                        type: "\"center\" | \"free\"",
                        defaultValue: "\"center\"",
                        name: "alignContent"
                    },
                    {
                        description: t("modalPage.propOnOverlayPress"),
                        type: "({ closeAnimation }) => void",
                        name: "onOverlayPress"
                    },
                    {
                        description: t("modalPage.propIsDisabledOverlay"),
                        name: "isDisabledOverlay",
                        defaultValue: "false",
                        type: "boolean"
                    },
                    {
                        description: t("modalPage.propIsOverlayVisible"),
                        name: "isOverlayVisible",
                        defaultValue: "true",
                        type: "boolean"
                    },
                    {
                        description: t("modalPage.propOnClosed"),
                        type: "({ id }) => void",
                        name: "onClosed"
                    },
                    {
                        description: t("modalPage.propIsAnimated"),
                        defaultValue: "true",
                        name: "isAnimated",
                        type: "boolean"
                    },
                    {
                        description: t("modalPage.propIsScaleAnimated"),
                        defaultValue: "true (center), false (free)",
                        name: "isScaleAnimated",
                        type: "boolean"
                    },
                    {
                        description: t("modalPage.propIsScaleAnimatedOnlyOpen"),
                        name: "isScaleAnimatedOnlyOpen",
                        defaultValue: "true",
                        type: "boolean"
                    },
                    {
                        description: t("modalPage.propOpenAnimationDelay"),
                        name: "openAnimationDelay",
                        type: "number"
                    },
                    {
                        description: t("modalPage.propCloseAnimationDelay"),
                        name: "closeAnimationDelay",
                        type: "number"
                    },
                    {
                        description: t("modalPage.propRole"),
                        type: "\"dialog\" | \"alertdialog\"",
                        defaultValue: "\"dialog\"",
                        name: "role"
                    },
                    {
                        description: t("modalPage.propAccessibilityLabel"),
                        name: "accessibilityLabel",
                        type: "string"
                    },
                    {
                        description: t("modalPage.propAriaLabel"),
                        name: "aria-label",
                        type: "string"
                    },
                    {
                        description: t("modalPage.propAriaLabelledby"),
                        name: "aria-labelledby",
                        type: "string"
                    },
                    {
                        description: t("modalPage.propAriaDescribedby"),
                        name: "aria-describedby",
                        type: "string"
                    },
                    {
                        description: t("modalPage.propInitialFocusRef"),
                        type: "RefObject<HostInstance>",
                        name: "initialFocusRef"
                    },
                    {
                        description: t("modalPage.propReturnFocusRef"),
                        type: "RefObject<HostInstance>",
                        name: "returnFocusRef"
                    },
                    {
                        description: t("modalPage.propIsWorkWithPortal"),
                        name: "isWorkWithPortal",
                        defaultValue: "true",
                        type: "boolean"
                    },
                    {
                        description: t("modalPage.propPortalName"),
                        name: "portalName",
                        type: "string"
                    },
                    {
                        description: t("modalPage.propOverlayProps"),
                        name: "overlayProps",
                        type: "ViewProps"
                    },
                    {
                        description: t("modalPage.propIsContentRequired"),
                        name: "isContentRequired",
                        type: "boolean"
                    },
                    {
                        type: "{ themeKey, paletteKey, sharpness, gapPropagation }",
                        description: t("modalPage.propCustomTheme"),
                        name: "customTheme"
                    },
                    {
                        description: t("modalPage.propCustomLocalize"),
                        type: "{ activeLocale }",
                        name: "customLocalize"
                    }
                ]}
            />
            <Paragraph
                isMuted={true}
            >
                {t("modalPage.propsNote")}
            </Paragraph>
        </Section>
        <ExampleSection
            name={NAME}
        />
    </Article>;
};
export default ModalPage;
